import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { ComparisonTypeEnum } from "../../Enum";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import AutocompleteInput from "../AutocompleteInput/AutocompleteInput";
import PhaseSelector from "../PhaseSelector/PhaseSelector";

type ComparisonRuleFormProps = {
  onSubmit: (data: any) => void;
  manifestNames: Record<string, string>;
};

const ComparisonRuleForm: React.FC<ComparisonRuleFormProps> = ({
  onSubmit,
  manifestNames,
}) => {
  const validationSchema = Yup.object().shape({
    firstVariableName: Yup.string().required("First variable is required"),
    secondVariableName: Yup.string().required("Second variable is required"),
    comparisonType: Yup.number()
      .typeError("Comparison type must be selected")
      .required("Comparison type is required"),
    errorMessage: Yup.string().required("Error message is required"),
    applicablePhaseIds: Yup.array().of(Yup.number()),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      firstVariableName: "",
      secondVariableName: "",
      comparisonType: 0,
      errorMessage: "",
      applicablePhaseIds: [],
    },
  });

  const selectedPhases = watch("applicablePhaseIds") as number[];

  const handleFormSubmit = (data: any) => {
    onSubmit(data);
    // Reset form after successful submission
    reset();
  };

  const handleReset = () => {
    reset();
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">
        Create Comparison Rule
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First Variable Name */}
        <Controller
          name="firstVariableName"
          control={control}
          render={({ field }) => (
            <AutocompleteInput
              id="firstVariableName"
              label="First Variable"
              placeholder="Search for first variable..."
              manifestNames={manifestNames}
              value={field.value}
              onChange={(name, id) => {
                field.onChange(id);
              }}
              error={errors.firstVariableName?.message}
              required
            />
          )}
        />

        {/* Second Variable Name */}
        <Controller
          name="secondVariableName"
          control={control}
          render={({ field }) => (
            <AutocompleteInput
              id="secondVariableName"
              label="Second Variable"
              placeholder="Search for second variable..."
              manifestNames={manifestNames}
              value={field.value}
              onChange={(name, id) => {
                field.onChange(id);
              }}
              error={errors.secondVariableName?.message}
              required
            />
          )}
        />

        {/* Comparison Type */}
        <div>
          <label
            htmlFor="comparisonType"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Comparison Type <span className="text-red-500">*</span>
          </label>
          <select
            id="comparisonType"
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.comparisonType ? "border-red-500" : "border-gray-300"
            }`}
            {...register("comparisonType")}
          >
            <option value="">Select comparison type...</option>
            <option value={ComparisonTypeEnum.GreaterThan}>
              Greater Than (&gt;)
            </option>
            <option value={ComparisonTypeEnum.LessThan}>
              Less Than (&lt;)
            </option>
            <option value={ComparisonTypeEnum.EqualTo}>Equal To (=)</option>
            <option value={ComparisonTypeEnum.NotEqualTo}>
              Not Equal To (≠)
            </option>
            <option value={ComparisonTypeEnum.GreaterOrEqualTo}>
              Greater or Equal To (≥)
            </option>
            <option value={ComparisonTypeEnum.LessOrEqualTo}>
              Less or Equal To (≤)
            </option>
          </select>
          {errors.comparisonType && (
            <p className="text-red-500 text-xs mt-1">
              {errors.comparisonType.message}
            </p>
          )}
        </div>

        {/* Error Message */}
        <div className="md:col-span-2">
          <label
            htmlFor="errorMessage"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Error Message <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="errorMessage"
            className={`w-full border rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.errorMessage ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Enter error message to display when rule fails..."
            {...register("errorMessage")}
          />
          {errors.errorMessage && (
            <p className="text-red-500 text-xs mt-1">
              {errors.errorMessage.message}
            </p>
          )}
        </div>

        {/* Applicable Phases */}
        <div className="md:col-span-2">
          <Controller
            name="applicablePhaseIds"
            control={control}
            render={({ field }) => (
              <PhaseSelector
                selectedPhases={selectedPhases || []}
                onChange={field.onChange}
                error={errors.applicablePhaseIds?.message}
              />
            )}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={handleReset}
          className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
        >
          Reset
        </button>
        <button
          type="submit"
          className="px-6 py-2.5 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-600 transition"
        >
          Create Rule
        </button>
      </div>
    </form>
  );
};

export default ComparisonRuleForm;